import React, { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import usePreDialogValidation from '../../../hooks/usePreDialogValidation';
import { DBDeal } from '../../../types/deal';
import { base64ToBlob } from '../../../utils';
import { openBlobInNewTab } from '../../../utils/openBlobUrl';
import { ValidationField } from '../../../utils/validationUtils';
import { ErrorMessageSnackbar } from '../../ErrorMessageSnackbar';

interface CreatePurchaseCerfaProps {
  deal: DBDeal;
  isMandate: boolean;
  onRefresh?: () => void;
}

const CreatePurchaseCerfa: React.FC<CreatePurchaseCerfaProps> = ({ deal, isMandate, onRefresh }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingVehicleInfo, setIsSavingVehicleInfo] = useState(false);
  const [vin, setVin] = useState<string>('');
  const [numFormule, setNumFormule] = useState<string>('');

  const validation = usePreDialogValidation();

  const queryClient = useQueryClient();

  // Initialiser VIN et num_formule UNE SEULE FOIS au montage du composant
  useEffect(() => {
    setVin(deal.vin || '');
    setNumFormule(deal.num_formule || '');
  }, []); // Empty dependency array = run only once on mount

  const fetchAndValidate = async (): Promise<{
    fields: ValidationField[];
    dealId: string | number;
    context?: string;
  }> => {
    const registration = deal.registration || deal.registration_plate;
    return {
      dealId: deal.id,
      context: "Certificat d'achat",
      fields: [{ key: 'registration', value: registration }],
    };
  };

  const handleDialogOpen = () => {
    validation.validateAndOpen(fetchAndValidate, () => setIsDialogOpen(true));
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSaveVehicleInfo = async () => {
    setIsSavingVehicleInfo(true);

    try {
      // 1. Mise à jour VIN et num_formule dans car_deals
      await makeAuthenticatedApiCall('patch', `/api/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      // 2. Mise à jour VIN et num_formule dans Pipedrive
      await makeAuthenticatedApiCall('patch', `/api/pipedrive/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      validation.openSnackbar('✅ Informations du véhicule mises à jour avec succès', 'success');

      // Mettre à jour les valeurs locales immédiatement
      setVin(vin || '');
      setNumFormule(numFormule || '');

      // Invalider ET refetch les queries pour une actualisation complète
      queryClient.invalidateQueries({ queryKey: ['useArbitratorDeals'] });
      queryClient.invalidateQueries({ queryKey: ['useDatabaseDeals'] });

      await queryClient.refetchQueries({ queryKey: ['useArbitratorDeals'] });
      await queryClient.refetchQueries({ queryKey: ['useDatabaseDeals'] });

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      const rawSaveData = error?.response?.data;
      const saveErrorMessage =
        (typeof rawSaveData === 'string' ? rawSaveData : null) ||
        rawSaveData?.error ||
        rawSaveData?.message ||
        error?.message ||
        'Erreur lors de la sauvegarde';
      validation.openSnackbar(saveErrorMessage, 'error');
    } finally {
      setIsSavingVehicleInfo(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsLoading(true);
    try {
      const updatedDeal = {
        ...deal,
        isMandate,
        vin: vin || deal.vin,
        num_formule: numFormule || deal.num_formule,
      };

      if (isMandate) {
        // Pipeline B: upload to Firebase + return base64
        const response = await makeAuthenticatedApiCall(
          'post',
          `/api/deals/${deal.id}/pipeline-b/generate-and-upload-purchase-cerfa`,
          {
            saleDateTime: new Date().toISOString(),
            num_formule: updatedDeal.num_formule,
            registration: deal.registration || deal.registration_plate,
            isMandate: true,
          },
        );
        const { base64 } = response?.data as { success: boolean; firebaseUrl: string; base64: string };

        openBlobInNewTab(base64ToBlob(base64, 'application/pdf'));

        // Notify SendProcurementRequestButton that purchase cerfa was uploaded
        window.dispatchEvent(new CustomEvent('purchaseCerfaPdfUploaded'));
      } else {
        // Pipeline A: generate locally only
        const response = await makeAuthenticatedApiCall('post', `/api/misiv/cerfa/purchase`, updatedDeal);
        const base64String = response?.data as string;

        openBlobInNewTab(base64ToBlob(base64String, 'application/pdf'));
      }

      handleDialogClose();
    } catch (error: any) {
      const rawData = error?.response?.data;
      const errorMessage =
        (typeof rawData === 'string' ? rawData : null) ||
        rawData?.details ||
        rawData?.message ||
        rawData?.error ||
        error?.message ||
        'Erreur lors de la génération du document';
      validation.openSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip placement="top" title={validation.tooltipError || ''}>
        <span>
          <Button
            className="dealStageActionsButton"
            variant="contained"
            onClick={handleDialogOpen}
            disabled={validation.disabledButton}
          >
            {isMandate ? "✍️ Déclaration d'achat à signer" : "ℹ️ Certificat d'achat"}
          </Button>
        </span>
      </Tooltip>
      {validation.tooltipError && (
        <Typography color="error" sx={{ fontSize: 12, marginTop: 1, textAlign: 'center' }}>
          {validation.tooltipError}
        </Typography>
      )}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{isMandate ? "Déclaration d'achat à signer" : "Certificat d'achat"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                className="inputElement"
                margin="dense"
                id="vin"
                label="VIN"
                type="text"
                fullWidth
                value={vin}
                onChange={(e) => setVin(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                className="inputElement"
                margin="dense"
                id="num_formule"
                label="Numéro de formule"
                type="text"
                fullWidth
                value={numFormule}
                onChange={(e) => setNumFormule(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button className="secondaryDealActionButton" onClick={handleDialogClose} variant="outlined">
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            variant="outlined"
            onClick={handleSaveVehicleInfo}
            disabled={isSavingVehicleInfo}
            sx={{
              border: '2px solid #FF6060 !important',
              color: 'black !important',
              backgroundColor: 'white !important',
              '&:hover': {
                backgroundColor: '#fff5f5 !important',
                border: '2px solid #FF6060 !important',
              },
            }}
          >
            {isSavingVehicleInfo ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                Enregistrement...
              </>
            ) : (
              '💾 Enregistrer'
            )}
          </Button>
          <Button className="dealActionButton" variant="contained" onClick={handleGeneratePDF} disabled={isLoading}>
            {isLoading ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                Génération...
              </>
            ) : (
              '📄 Générer'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorMessageSnackbar
        open={validation.snackbarOpen}
        message={validation.snackbarMessage}
        handleClose={() => validation.setSnackbarOpen(false)}
        severity={validation.severity}
      />
    </>
  );
};

export default CreatePurchaseCerfa;
